import { useQueryClient } from '@tanstack/react-query';
import { Formik, FormikErrors } from 'formik';
import { useState } from 'react';

import { ContractMessage, ContractState } from '../../../types/ContractMessage';
import { CustomerCompanyMessage } from '../../../types/CustomerCompanyMessage';
import { useToasts } from '../../cdl/Toast/useToasts';
import { validateContract as validateContractPayload } from '../../common/api/clients/contracts.api';
import { queries } from '../../common/api/queryKeys/queries';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { useContractPriceListCreate } from '../../contracts/common/hooks/useContractPriceListCreate';
import { ContractDrawerForm } from '../../contracts/form/ContractDrawerForm';
import {
    getInitialContractFormValues,
    mapBackendValidationErrors,
    toContractPayload,
    toPriceListPayload,
    validateContractForm,
} from '../../contracts/form/contractForm.helpers';
import { ContractFormValues } from '../../contracts/form/contractForm.types';
import { useContractCreate } from '../../contracts/form/hooks/useContractCreate';

interface CreateContractDrawerProps {
    onSuccess: (contract: ContractMessage) => void;
    sourceContract?: ContractMessage;
}

export const CreateContractDrawer = ({ onSuccess, sourceContract }: CreateContractDrawerProps) => {
    const { addToast, addErrorToast } = useToasts();
    const queryClient = useQueryClient();
    const createMutation = useContractCreate();
    const createPriceListsMutation = useContractPriceListCreate();
    const role = useRole();
    const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
    const roleCustomerIds = role.getCompanies().map((company) => company.id);

    const getCustomerIdsFromSelectedGroup = (customerGroupId: string): string[] => {
        if (!customerGroupId) {
            return [];
        }

        const query = queries.customers.list({ groupId: customerGroupId });
        const customers = queryClient.getQueryData<CustomerCompanyMessage[]>(query.queryKey) ?? [];
        return customers.map((company) => company.id);
    };

    const getReferenceCustomerIds = (customerGroupId?: string) => {
        return role.isSuperAdmin() ? getCustomerIdsFromSelectedGroup(customerGroupId ?? '') : roleCustomerIds;
    };

    const mapEmptyCustomerIdsToAllSelected = (customerIds: string[], customerGroupId?: string) => {
        const referenceCustomerIds = getReferenceCustomerIds(customerGroupId);
        const hasAllSelected =
            referenceCustomerIds.length > 0 &&
            referenceCustomerIds.length === customerIds.length &&
            referenceCustomerIds.every((customerId) => customerIds.includes(customerId));

        return hasAllSelected ? [] : customerIds;
    };

    const validateContract = async (values: ContractFormValues): Promise<FormikErrors<ContractFormValues>> => {
        const errors = validateContractForm(values);

        if (role.isSuperAdmin() && !values.customerGroupId) {
            errors.customerGroupId = translate('contracts.form.validation.customerGroupRequired');
        }

        if (Object.keys(errors).length) {
            return errors;
        }

        let customerIds = values.customerIds;
        if (!customerIds.length) {
            if (role.isSuperAdmin()) {
                customerIds = getCustomerIdsFromSelectedGroup(values.customerGroupId);
            } else {
                customerIds = roleCustomerIds;
            }
        }
        try {
            const validationPayload = toContractPayload({ ...values, customerIds });
            const validationResponse = await validateContractPayload(validationPayload);
            const contractErrors = mapBackendValidationErrors(validationResponse, values);

            return contractErrors;
        } catch {
            addErrorToast(
                values.state === ContractState.DRAFT
                    ? translate('contracts.form.toast.createDraftError')
                    : translate('contracts.form.toast.publishError')
            );

            return { _validation: 'request_failed' } as FormikErrors<ContractFormValues>;
        }
    };

    const initialCustomerIdsForCopy = (() => {
        if (!sourceContract) {
            return undefined;
        }

        return mapEmptyCustomerIdsToAllSelected(sourceContract.customerIds, sourceContract.customerGroupId);
    })();

    const handleSubmit = async (values: ContractFormValues) => {
        let customerIds = values.customerIds;
        if (!customerIds.length) {
            if (role.isSuperAdmin()) {
                customerIds = getCustomerIdsFromSelectedGroup(values.customerGroupId);
            } else {
                customerIds = roleCustomerIds;
            }
        }
        const payload = toContractPayload({ ...values, customerIds });
        const isDraft = values.state === ContractState.DRAFT;
        if (isDraft) {
            setIsDraftSubmitting(true);
        }

        try {
            const contract = await createMutation.mutateAsync(payload);

            if (values.priceLists.length > 0) {
                await createPriceListsMutation.mutateAsync({
                    contractId: contract.id,
                    priceLists: toPriceListPayload(values),
                });
            }

            addToast(
                isDraft
                    ? translate('contracts.form.toast.createDraftSuccess')
                    : translate('contracts.form.toast.publishSuccess')
            );
            onSuccess(contract);
        } catch {
            addErrorToast(
                isDraft
                    ? translate('contracts.form.toast.createDraftError')
                    : translate('contracts.form.toast.publishError')
            );
        } finally {
            if (isDraft) {
                setIsDraftSubmitting(false);
            }
        }
    };

    return (
        <Formik
            initialValues={getInitialContractFormValues(sourceContract, !!sourceContract, initialCustomerIdsForCopy)}
            validate={validateContract}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            <ContractDrawerForm
                showPublishButton
                showCustomerGroupSelect
                showPriceLists
                isDraftSubmitting={isDraftSubmitting}
            />
        </Formik>
    );
};
